import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import { filterMarketplaceCustomers, getMarketplaceCustomers } from '../../data';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const DASHLET_COLOR = '#003366';

const SOURCE_LABELS = {
    registration: 'Signed Up',
    checkout: 'Checkout',
};

function formatNumber(amount) {
    return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function MarketplaceCustomers() {

    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(null);

    const initialize = async () => {
        setLoading(true);
        const response = await getMarketplaceCustomers();
        if (response) {
            setDataSource(response);
        }
        setLoading(false);
    }

    useEffect(() => {
        initialize();
    }, [])

    const handleSearch = async (values = {}) => {
        setLoading(true);

        const params = { ...values };

        // The API takes the two ends of the range as separate dates.
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.from = dateRange[0].format('YYYY-MM-DD');
            params.to = dateRange[1].format('YYYY-MM-DD');
        }

        // Leave empty controls out entirely, otherwise the nullable rules still
        // narrow the result set on a blank value.
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        const response = await filterMarketplaceCustomers(params);
        if (response) {
            setDataSource(response);
        }
        setLoading(false);
    }

    const handleClear = () => {
        form.resetFields();
        setDateRange(null);
        initialize();
    }

    const stats = useMemo(() => {
        const total = dataSource.length;
        const registered = dataSource.filter((customer) => customer.registered).length;
        const withOrders = dataSource.filter((customer) => Number(customer.orders_count || 0) > 0).length;
        const totalSpent = dataSource.reduce((sum, customer) => sum + Number(customer.total_spent || 0), 0);
        const totalOrders = dataSource.reduce((sum, customer) => sum + Number(customer.orders_count || 0), 0);

        return [
            { heading: 'Total Customers', value: total },
            { heading: 'Registered', value: registered },
            { heading: 'Guests', value: total - registered },
            { heading: 'Customers With Orders', value: withOrders },
            { heading: 'Total Orders', value: totalOrders },
            { heading: 'Total Spent', value: formatNumber(totalSpent) },
        ];
    }, [dataSource]);

    const columns = [
        {
            title: 'S.No',
            key: 'index',
            render: (text, record, index) => <Text>{index + 1}</Text>,
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (text, record) => (
                <>
                    <Text strong>{record.name || '-'}</Text>
                    <br />
                    <Text type="secondary">{record.email || 'No email'}</Text>
                </>
            ),
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone) => <Text>{phone || '-'}</Text>,
        },
        {
            title: 'Location',
            key: 'location',
            render: (text, record) => {
                const location = [record.city, record.state, record.country].filter(Boolean).join(', ');
                return (
                    <>
                        <Text>{location || '-'}</Text>
                        {record.address && (
                            <>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>{record.address}</Text>
                            </>
                        )}
                    </>
                );
            },
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            filters: [
                { text: 'Signed Up', value: 'registration' },
                { text: 'Checkout', value: 'checkout' },
            ],
            onFilter: (value, record) => record.source === value,
            render: (source) => <Tag>{SOURCE_LABELS[source] || source}</Tag>,
        },
        {
            title: 'Account',
            dataIndex: 'registered',
            key: 'registered',
            filters: [
                { text: 'Registered', value: true },
                { text: 'Guest', value: false },
            ],
            onFilter: (value, record) => Boolean(record.registered) === value,
            render: (registered) => (
                <Tag color={registered ? 'green' : 'orange'}>{registered ? 'Registered' : 'Guest'}</Tag>
            ),
        },
        {
            title: 'Orders',
            dataIndex: 'orders_count',
            key: 'orders_count',
            sorter: (a, b) => Number(a.orders_count || 0) - Number(b.orders_count || 0),
            render: (ordersCount) => <Text style={{ float: 'right' }}>{Number(ordersCount || 0)}</Text>,
        },
        {
            title: 'Total Spent',
            dataIndex: 'total_spent',
            key: 'total_spent',
            sorter: (a, b) => Number(a.total_spent || 0) - Number(b.total_spent || 0),
            render: (totalSpent) => <Text strong style={{ float: 'right' }}>{formatNumber(totalSpent)}</Text>,
        },
        {
            title: 'Last Order',
            dataIndex: 'last_order_at',
            key: 'last_order_at',
            render: (lastOrderAt) => (
                lastOrderAt
                    ? (
                        <>
                            <Text>{moment(lastOrderAt).format('DD-MMM-Y')}</Text>
                            <br />
                            <Text type="secondary">{moment(lastOrderAt).format('hh:mm A')}</Text>
                        </>
                    )
                    : <Text type="secondary">Never</Text>
            ),
        },
        {
            title: 'Added On',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
            render: (createdAt) => <Text>{createdAt ? moment(createdAt).format('DD-MMM-Y') : '-'}</Text>,
        },
    ];

    const renderFilter = (
        <Form layout={'vertical'} form={form} onFinish={handleSearch}>
            <Row gutter={16}>
                <Col span={6}>
                    <Form.Item label="Search" name="search">
                        <Input allowClear placeholder="Name, email or phone" />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label="Source" name="source">
                        <Select allowClear placeholder="Select Source">
                            <Option value="registration">Signed Up</Option>
                            <Option value="checkout">Checkout</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label="Account" name="registered">
                        <Select allowClear placeholder="Select Account Type">
                            <Option value={true}>Registered</Option>
                            <Option value={false}>Guest</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label="Orders" name="has_orders">
                        <Select allowClear placeholder="Select">
                            <Option value={true}>Has Orders</Option>
                            <Option value={false}>No Orders</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="Added Between">
                        <RangePicker style={{ width: '100%' }} value={dateRange} onChange={(value) => setDateRange(value)} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="City" name="city">
                        <Input allowClear placeholder="Enter City" />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="Country" name="country">
                        <Input allowClear placeholder="Enter Country" />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={<Text>&nbsp;</Text>}>
                        <Space>
                            <Button htmlType="submit" type="primary">Search</Button>
                            <Button onClick={handleClear}>Clear</Button>
                        </Space>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    )

    return (
        <Card>
            <Title level={3}>Marketplace Customers</Title>

            <Row gutter={[16, 18]}>
                {stats.map((card, index) => (
                    <Col span={8} key={index}>
                        <Card style={{ backgroundColor: DASHLET_COLOR }}>
                            <Title level={5} style={{ color: '#FFFFFF', margin: 0 }}>{card.heading}</Title>
                            <p style={{ color: '#FFFFFF', margin: 0, textAlign: 'right', fontSize: 18 }}>{card.value}</p>
                        </Card>
                    </Col>
                ))}
            </Row>

            <br />

            {renderFilter}

            <Table
                dataSource={dataSource}
                columns={columns}
                title={() => <Title level={4}>All Customers</Title>}
                loading={loading}
                rowKey="id"
            />
        </Card>
    )
}
